<template>
  <b-modal
    ref="self"
    class="card-form-modal"
    :active.sync="active"
    :custom-class="'dialog partner-modal'"
    has-modal-card
    trap-focus
    :destroy-on-hide="true"
    aria-role="dialog"
    scroll="keep"
    aria-modal
  >
    <template>
      <validation-observer v-slot="{ handleSubmit }">
        <div class="modal-card">
          <header class="modern-card__header">
            Edit partner
            <b-button
              class="modern-card__close-button"
              aria-label="close"
              icon-right="close"
              @click="cancel"
            />
          </header>
          <section ref="modernCardBody" class="modal-card-body">
            <form>
              <div v-if="partnerHasUser" class="partner-user-info">
                {{ partner.firstName }} {{ partner.lastName }} (ID {{ partner.id }})
              </div>

              <div v-else>
                <div class="columns">
                  <div class="column is-half">
                    <ModernField
                      v-model="userFields.firstName"
                      label="First Name*"
                      rules="required|name|max:100"
                    >
                      <ModernTextInput
                        v-model="userFields.firstName"
                        maxlength="100"
                      />
                    </ModernField>
                  </div>

                  <div class="column is-half">
                    <ModernField
                      v-model="userFields.lastName"
                      label="Last Name*"
                      rules="required|name|max:100"
                    >
                      <ModernTextInput
                        v-model="userFields.lastName"
                        maxlength="100"
                      />
                    </ModernField>
                  </div>
                </div>

                <div class="columns">
                  <div class="column is-half">
                    <ModernField
                      v-model="userFields.email"
                      label="E-mail (login)*"
                      rules="email|required|max:250"
                    >
                      <ModernEmailInput v-model="userFields.email" />
                    </ModernField>
                  </div>

                  <div class="column is-half">
                    <ModernField
                      v-model="userFields.phone"
                      label="Phone*"
                      rules="phone|required"
                    >
                      <ModernPhoneInput v-model="userFields.phone" />
                    </ModernField>
                  </div>

                </div>

                <div class="columns">
                  <div class="column is-full">
                    <ModernField
                      v-model="userFields.companyName"
                      label="Company Name"
                      rules="max:240"
                    >
                      <ModernTextInput
                        v-model="userFields.companyName"
                        maxlength="240"
                      />
                    </ModernField>
                  </div>
                </div>
              </div>

              <div>
                <div class="columns">
                  <div class="column is-4">
                    <ModernField
                      v-model="partnerFields.statusId"
                      label="Status"
                      rules="required"
                    >
                      <b-select
                        v-model="partnerFields.statusId"
                      >
                        <option
                          v-for="(status, index) in allowedStatuses"
                          :value="status.id"
                          :key="index"
                        >
                          {{ status.value }}
                        </option>
                      </b-select>
                    </ModernField>
                  </div>

                  <div class="column is-8">
                    <div class="users-partner-form-plan">
                      <div class="users-partner-form-plan--field">
                        <ModernField
                          v-model="partnerFields.plan"
                          :label="'Plan' + (requiredWhenStatusIsActive ? '*' : '')"
                          :rules="requiredWhenStatusIsActive"
                        >
                          <b-select-ext
                            class="custom-selector"
                            search-placeholder="Search by Name, Discount or Commissions..."
                            :selected-item="partnerFields.plan"
                            :options="plans"
                            :autocomplete="true"
                            :disabled="planIsDisabled"
                            :filterCallback="planFilterCallback"
                            @input="onPlanFieldValueChange"
                            @showListItems="scrollToHtmlElement"
                          >
                            <template #optionContent="{ option }">
                                <span class="users-partner-form-select-plan-item">
                                  {{ option.name }}
                                  <UsersPartnerPlanExtraInfo :plan="option" />
                                </span>
                            </template>
                          </b-select-ext>
                        </ModernField>
                      </div>

                      <UsersPartnerPlanExtraInfo :plan="partnerFields.plan" />
                    </div>
                  </div>
                </div>

                <div class="columns">
                  <div class="column is-full">
                    <ModernField
                        v-model="partnerFields.leadOwner"
                        :label="'Lead owner' + (requiredWhenStatusIsActive ? '*' : '')"
                        :rules="requiredWhenStatusIsActive"
                    >
                      <b-select-ext
                          :selectedItem="partnerFields.leadOwner"
                          :options="managers"
                          :autocomplete="true"
                          @input="onLeadOwnerFieldValueChange"
                          @showListItems="scrollToHtmlElement"
                      />
                    </ModernField>
                  </div>
                </div>
              </div>
            </form>

            <div style="height: 240px"></div>
          </section>
          <footer class="modal-card-foot">
            <button class="button" type="button" @click="cancel">Cancel</button>
            <button
                class="button is-success"
                @click="handleSubmit(submit)"
            >
              Update
            </button>
          </footer>
        </div>
      </validation-observer>
    </template>
  </b-modal>
</template>

<script>
import { get } from 'lodash';
import { mapGetters, mapMutations } from 'vuex';
import { read as getPartnerPlans } from '@/api/plan_partners';
import {
  manageUpdate as updatePartner,
  manageRead as getPartner,
} from '@/api/partner';
import { search as getManagers } from '@/api/manager';
import { PARTNERS_MODIFY_MANAGE } from '@/const/privileges';
import {
  PARTNER_STATUS_ACTIVE_ID,
  PARTNER_STATUS_CANCELED_ID,
  PARTNER_STATUS_PENDING_ID,
} from '@/const/constants';
import ModernEmailInput from '@/components/Form/Input/ModernEmailInput.vue';
import ModernPhoneInput from '@/components/Form/Input/ModernPhoneInput.vue';
import ModernTextInput from '@/components/Form/Input/ModernTextInput.vue';
import ModernField from '@/components/Form/Field/ModernField.vue';
import BSelectExt from '@/components/BSelectExt.vue';
import UsersPartnerPlanExtraInfo
  from '@/views/Manage/Users/UsersPartnerForm/UsersPartnerPlanExtraInfo.vue';
import * as MUTATIONS from '@/store/mutations';

export default {
  components: {
    UsersPartnerPlanExtraInfo,
    ModernTextInput,
    ModernField,
    ModernEmailInput,
    ModernPhoneInput,
    BSelectExt,
  },
  name: 'PartnerUpdateForm',
  props: ['active', 'statuses', 'id'],
  data() {
    const userFields = {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      companyName: null,
    };

    const partnerFields = {
      statusId: null,
      plan: null,
      leadOwner: null,
    };

    return {
      userFields: { ...userFields },
      partnerFields: { ...partnerFields },
      maxTeamUsersByTier: null,
      confirmModal: null,
      partner: null,
      plans: [],
      managers: [],
      allowedStatuses: [],
    };
  },
  methods: {
    ...mapMutations({ preloader: MUTATIONS.SET_PRELOADER }),
    planFilterCallback(search, plan) {
      const values = [
        plan.name,
        plan.discountUnit === '$' ? `${plan.discountUnit}${plan.discount}` : `${plan.discount}${plan.discountUnit}`,
        plan.commissionUnit === '$' ? `${plan.commissionUnit}${plan.commission}` : `${plan.commission}${plan.commissionUnit}`,
      ];

      return values.reduce(
        (result, value) => result || value.toLowerCase().includes(search),
        false,
      );
    },
    submit() {
      const obj = this.partnerHasUser ? {} : { ...this.userFields };
      obj.statusId = this.partnerFields.statusId;
      obj.planId = this.partnerFields.plan?.id ?? null;
      obj.leadOwnerId = this.partnerFields.leadOwner?.id ?? null;
      if (!obj.companyName?.trim()) {
        obj.companyName = null;
      }

      this.preloader(true);
      updatePartner(this.partner.id, obj)
        .then(() => {
          this.active = false;
          this.$buefy.toast.open({ message: 'Partner was updated successfully', type: 'is-success', duration: 3000 });
          this.$emit('success-submit');
        })
        .catch((err) => {
          const isUIErrorMessage = get(err, 'response.data.is_ui_error_message', true);
          if (isUIErrorMessage && get(err, 'response.status', 500) !== 500) {
            const error = get(err, 'response.data.error', null);
            const message = get(err, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com');
            if (error) {
              this.$buefy.snackbar.open({
                message,
                position: 'is-top',
                indefinite: true,
                actionText: ' ',
                type: 'is-danger',
              });
            } else {
              this.$buefy.snackbar.open({
                message: 'An unexpected error occurred, please contact support@enhancify.com',
                position: 'is-top',
                indefinite: true,
                actionText: ' ',
                type: 'is-danger',
              });
            }
          }
        })
        .finally(() => {
          this.preloader(false);
        });
    },
    cancel() {
      this.active = false;
      this.$emit('close');
    },
    setFieldsFromPartner() {
      this.userFields.firstName = this.partner.firstName;
      this.userFields.lastName = this.partner.lastName;
      this.userFields.email = this.partner.email;
      this.userFields.phone = this.partner.phone;
      this.userFields.companyName = this.partner.companyName;
      this.partnerFields.statusId = this.partner.status.id;

      if (this.partner.status.id === PARTNER_STATUS_PENDING_ID) {
        this.allowedStatuses = this.statuses;
      } else {
        this.allowedStatuses = this.statuses
          .filter((item) => item.id !== PARTNER_STATUS_PENDING_ID);
      }

      const plan = this.partner?.plan;
      const partnerPlanExistInPlans = this.plans.find((item) => item.id === this.partner?.plan?.id);
      const leadOwner = this.managers.find((item) => item.id === this.partner?.leadOwner?.id);

      if (plan && !partnerPlanExistInPlans) {
        this.plans.push(plan);
        this.plans.sort((a, b) => {
          if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
          if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
          return 0;
        });
      }

      this.partnerFields.plan = plan;
      this.partnerFields.leadOwner = leadOwner ?? null;
    },
    onPlanFieldValueChange(value) {
      this.partnerFields.plan = value;
    },
    onLeadOwnerFieldValueChange(value) {
      this.partnerFields.leadOwner = value;
    },
    scrollToHtmlElement(htmlElement) {
      const { modernCardBody } = this.$refs;

      if (!htmlElement || !modernCardBody) {
        return;
      }

      this.$nextTick(() => {
        const y = modernCardBody.scrollTop
          + htmlElement.getBoundingClientRect().bottom
          - modernCardBody.getBoundingClientRect().bottom;

        modernCardBody.scrollTo(0, y + 10);
      });
    },
  },
  computed: {
    ...mapGetters(['authAuthenticator']),
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    partnerHasUser() {
      return this.partner && this.partner.user?.id;
    },
    requiredWhenStatusIsActive() {
      return this.partnerFields.statusId === PARTNER_STATUS_ACTIVE_ID ? 'required' : '';
    },
    planIsDisabled() {
      return this.partnerFields.statusId !== PARTNER_STATUS_ACTIVE_ID;
    },
  },
  watch: {
    'partnerFields.statusId': function parterFieldsStatusId(value) {
      if (value === PARTNER_STATUS_PENDING_ID) {
        this.partnerFields.plan = null;
      }
      if (value === PARTNER_STATUS_CANCELED_ID) {
        this.partnerFields.plan = this.partner?.plan;
      }
    },
  },
  async mounted() {
    if (!this.authAuthenticator(PARTNERS_MODIFY_MANAGE)) {
      this.$buefy.snackbar.open({
        message: 'Forbidden',
        position: 'is-top',
        indefinite: true,
        actionText: ' ',
        type: 'is-danger',
      });
      this.cancel();
      return;
    }

    this.preloader(true);

    const promises = [
      getPartner(this.id),
      getPartnerPlans({ order: 'name' }),
      getManagers({ orderBy: 'firstName' }),
    ];
    const [partnerResponse, plansResponse, managersResponse] = await Promise.all(promises);

    this.active = true;

    this.$nextTick(() => {
      this.partner = partnerResponse.data;
      this.plans = plansResponse.data.data;
      this.managers = managersResponse.data.map((item) => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName}`,
      }));

      this.setFieldsFromPartner();
    });

    this.preloader(false);
  },
};
</script>

<style lang="stylus">
.dialog.partner-modal .modal-card .modal-card-body .field.modern-field
  margin-top 0
  padding-bottom 1px
  & label
    font-weight 400

.modal.partner-modal .animation-content
  margin 0
  width 100%

.modal.partner-modal .modal-card-title
  font-size 30px
.modal.partner-modal .animation-content .modal-close.is-large
  display none
</style>

<style scoped lang="stylus">
.dialog .modal-card .modal-card-body .field
  margin-top 0

.dialog .modal-card
  padding 10px
  max-width 720px
  @media screen and (min-width: 600px)
    width 100%

.partner-user-info
  font-size 19px
  margin-bottom 25px

form
  .columns
    margin-top 0
    margin-bottom 0
  .column
    padding 0 15px

.field >>> .select
  height 48px !important

.control >>> .select.input
  &:not(.is-danger)
    border-color #8FA7B3!important
  &.disabled
    border-color #dfdfdf!important

.field >>> .label,.label
  font-weight 400

.button.is-success
  border none !important
</style>
